import {Result} from "@/app/Result";

/**
 * Generates a unique identifier by removing spaces and converting the given name to lowercase.
 *
 * @param {string} name - The input string for which the identifier is generated.
 * @return {string} The generated identifier in lowercase with spaces removed.
 */
export function getId(name: string): string {
    return name.replace(/\s/g, '').toLowerCase();
}

/**
 * Converts the given size in bytes to a string representing the size in megabytes (MB),
 * formatted to two decimal places.
 *
 * @param {number} bytes - The size in bytes to be converted.
 * @return {string} The size in megabytes (MB) as a string, formatted to two decimal places.
 */
export function toMb(bytes: number): string {
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
}

/**
 * Converts a given number of seconds into a time string in the format HH:mm:ss.
 *
 * @param {number} seconds - The number of seconds to be converted.
 * @return {string} A string representing the time in HH:mm:ss format.
 */
export function toTimeString(seconds: number): string {
    const hours = Math.floor(seconds / 3600).toString().padStart(2, '0');
    const minutes = Math.floor((seconds % 3600) / 60).toString().padStart(2, '0');
    const remainingSeconds = (seconds % 60).toString().padStart(2, '0');
    return `${hours}:${minutes}:${remainingSeconds}`;
}

/**
 * Processes raw data to generate table-compatible data with sorted and completed year entries.
 *
 * @param {Result} rawData - The raw input data containing category counts grouped by year.
 * @return {{ title: string, data: any }[]} An array of objects, each containing a title for the category
 * and transformed data with missing years filled as `null`, sorted alphabetically by category title.
 */
export function getTableData(rawData: Result): { title: string, data: any }[] {
    const podcasts: { title: string, data: any }[] = [];

    Object.entries(rawData.categoryCountYear).map(([category, subcategory], index) => {
        // Step 1: Find the range of years
        const allYears = Object.values(subcategory)
            .flatMap(obj => Object.keys(obj).map(year => parseInt(year)));
        const earliestYear = Math.min(...allYears);
        const latestYear = Math.max(...allYears);

        // Step 2: Prepare a list of all intermediate years
        const rangeOfYears = Array.from({length: latestYear - earliestYear + 1}, (_, i) => earliestYear + i);

        // Step 3: Transform the data with all missing years filled
        const transformed = Object.entries(subcategory).map(([name, yearCounts], index) => {
            const completeYears = rangeOfYears.reduce((acc, year) => {
                acc[year] = yearCounts[year] || null; // Use existing value or `null` for missing years
                return acc;
            }, {} as Record<number, number | null>);

            return {
                ...completeYears,
                id: index + 1,
                name,
            };
        });

        if (transformed.length != 0) {
            podcasts.push({
                title: category,
                data: transformed
            })
        }

    });

    // Step 4 sort result array by name
    podcasts.sort((a, b) => a.title.localeCompare(b.title));
    return podcasts;
}

/**
 * Converts the given HTML string to a processed HTML string with parsed line breaks,
 * removed empty or single `<br>` tags, and linked URLs.
 *
 * @param {string} html - The input HTML string to be processed.
 * @return {string} The processed HTML string with updates applied.
 */
export function getDescriptionHtml(html: string): string {
    const parsedLinebreaks = convertLinebreaks(html);
    const parsedEmptyLinebreaks = removeEmptyAndSingleBrPTags(parsedLinebreaks)
    return linkifyHTML(parsedEmptyLinebreaks);
}

/**
 * Removes empty `<p>` tags or `<p>` tags that contain only a single `<br>` element from the provided HTML string.
 *
 * @param {string} html - The input HTML string to process.
 * @return {string} The updated HTML string with empty or single `<br>` `<p>` tags removed.
 */
function removeEmptyAndSingleBrPTags(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Select all <p> elements
    const paragraphs = doc.querySelectorAll('p');
    paragraphs.forEach(p => {
        // If the <p> has no text content or only a single <br>, remove it
        if (!p.textContent?.trim() || (p.children.length === 1 && p.children[0].tagName === 'BR')) {
            p.remove();
        }
    });

    return doc.body.innerHTML;
}

/**
 * Converts raw text containing URLs into HTML by wrapping plain URLs with anchor tags.
 * This method processes the input string by removing existing anchors and ensuring that URLs are wrapped with appropriate HTML anchor tags.
 *
 * @param {string} text - The input string containing URLs to be converted into HTML anchor tags.
 * @return {string} The processed string with URLs replaced by HTML anchor tags.
 */
function linkifyHTML(text: string): string {
    const cleanedLinks = removeAnchorsAndLeaveUrls(text)
    return wrapPlainUrlsWithAnchors(cleanedLinks)
}

/**
 * Removes all anchor (<a>) tags from the provided HTML string, replacing them with their href attribute value if it exists.
 * If an anchor tag does not have an href attribute, it is removed entirely.
 *
 * @param {string} htmlString - The input HTML string from which anchor tags will be removed.
 * @return {string} The modified HTML string with anchor tags removed, leaving only the href values or plain text.
 */
export function removeAnchorsAndLeaveUrls(htmlString: string): string {
    // Use DOMParser to parse the HTML
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    // Find all anchor elements
    const anchors = doc.querySelectorAll("a");

    anchors.forEach(a => {
        // Extract the href attribute
        const hrefValue = a.getAttribute("href") || "";

        // Replace the <a> element with the plain href text, if found
        // If no href, remove the anchor completely
        if (hrefValue) {
            a.replaceWith(document.createTextNode(hrefValue));
        } else {
            a.remove();
        }
    });

    // Return the transformed HTML as a string
    return doc.body.innerHTML;
}

/**
 * Transforms all plaintext URLs starting with "https://" within an HTML string into clickable anchor (`<a>`) tags.
 * Each anchor element has attributes such as `target="_blank"` and `rel="noopener noreferrer"` for security purposes.
 *
 * @param {string} htmlString - The string containing HTML content that may include plaintext URLs.
 * @return {string} The modified HTML string with plaintext URLs wrapped in `<a>` tags.
 */
export function wrapPlainUrlsWithAnchors(htmlString: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");

    /**
     * A helper function that receives a text string, locates all URLs,
     * and returns a DocumentFragment with URLs turned into <a> elements.
     */
    function linkifyText(text: string): DocumentFragment {
        const fragment = new DocumentFragment();
        const urlRegex = /https:\/\/[^\s]+/gi;
        let lastIndex = 0;
        let match: RegExpExecArray | null;

        while ((match = urlRegex.exec(text)) !== null) {
            const url = match[0];

            // Append the text that appears before the URL
            const textBefore = text.slice(lastIndex, match.index);
            if (textBefore) {
                fragment.appendChild(document.createTextNode(textBefore));
            }

            // Create an <a> element for the found URL
            const linkEl = document.createElement("a");
            linkEl.href = url;
            linkEl.textContent = url;
            linkEl.target = "_blank";
            linkEl.rel = "noopener noreferrer";
            fragment.appendChild(linkEl);

            lastIndex = urlRegex.lastIndex;
        }

        // Append any remaining text after the last URL match
        if (lastIndex < text.length) {
            fragment.appendChild(document.createTextNode(text.slice(lastIndex)));
        }

        return fragment;
    }

    /**
     * Recursively traverse the DOM tree.
     * For each text node, replace any plain https:// URLs with <a> tags.
     */
    function traverseAndLinkify(node: Node): void {
        if (node.nodeType === Node.TEXT_NODE) {
            const originalText = node.textContent ?? "";
            const linkifiedFragment = linkifyText(originalText);

            // Only replace if there are changes
            const isDifferent =
                linkifiedFragment.childNodes.length > 1 ||
                (linkifiedFragment.firstChild?.nodeValue ?? "") !== originalText;

            if (isDifferent && node.parentNode) {
                node.parentNode.replaceChild(linkifiedFragment, node);
            }
        } else {
            // Recurse for child nodes
            node.childNodes.forEach(child => traverseAndLinkify(child));
        }
    }

    traverseAndLinkify(doc.body);

    return doc.body.innerHTML;
}

/**
 * Converts double line breaks in the input text to HTML `<br/>` tags.
 *
 * @param {string} text - The input text containing double line breaks to be converted.
 * @return {string} The modified text with double line breaks replaced by `<br/>` tags.
 */
function convertLinebreaks(text: string): string {
    return text.replace(/\n\n/g, '<br/>');
}
